import {UploadingFile} from '@contentlab/stores/contentLabStore';
import {AssetMetadata} from '@contentlab/types/asset';
import {FlowStatus} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import processAssetErrorCodes from '@contentlab/utils/processAssetErrorCodes';

/**
 * Maps an UploadingFile to AssetProps
 * @param uploadingFile - The uploading file to map
 * @param flowId - The flow ID (optional)
 * @param type - The asset type (optional)
 * @param errorCodes
 * @returns AssetProps object
 */
export default function mapFileToAssetProps(
  uploadingFile: UploadingFile,
  flowId?: string | null,
  type?: 'inputAsset' | 'outputAsset',
  errorCodes?: string[]
): AssetProps {
  // Convert FileStatus to FlowStatus
  const getFlowStatus = (status: UploadingFile['status']): FlowStatus => {
    switch (status) {
      case 'success':
        return FlowStatus.READY;
      case 'in_progress':
        return FlowStatus.IN_PROGRESS;
      case 'error':
        return FlowStatus.FAILED;
      default:
        return FlowStatus.PENDING;
    }
  };

  // Convert metadata from UploadingFile to AssetMetadata
  const metadata: AssetMetadata = {
    durationMillis: uploadingFile.metadata.duration_millis,
    fileSizeBytes: uploadingFile.file.size,
    width: uploadingFile.metadata.width,
    height: uploadingFile.metadata.height,
    aspectRatio: uploadingFile.metadata.aspect_ratio || undefined,
  };

  return {
    id: uploadingFile.id || uploadingFile.file.name,
    name: uploadingFile.file.name,
    isArchived: false,
    metadata,
    compositeId: flowId
      ? `${flowId}-${uploadingFile.id || uploadingFile.file.name}`
      : uploadingFile.id || uploadingFile.file.name,
    flowId: flowId || uploadingFile.id || uploadingFile.file.name,
    thumbnailUrl: undefined, // UploadingFile doesn't have thumbnailUrl
    url: undefined, // UploadingFile doesn't have url
    type,
    status: getFlowStatus(uploadingFile.status),
    errors: processAssetErrorCodes(errorCodes, uploadingFile.feature),
    uploadProgress: uploadingFile.progress,
    createdAt: new Date(),
    userInput: null, // Default value since UploadingFile doesn't have userInput
    userInputVersion: 1, // Default value since UploadingFile doesn't have userInputVersion
    progressPercentage: null, // Default value since UploadingFile doesn't have this property
    estimatedCompletionTime: null, // Default value since UploadingFile doesn't have this property
  };
}
